if (!customElements.get("bundle-products")) {
   class BundleProducts extends HTMLElement {
      constructor() {
         super();

         this.sectionId = this.dataset.sectionId;
         this.products = [];
         this.selectedVariants = new Map();

         // Elements
         this.addButtons = this.querySelectorAll(".bundle-products__add-button");
         this.totalPriceElement = this.querySelector("[data-total-price]");
         this.errorElement = this.querySelector(".bundle-products__error");
         this.errorMessageElement = this.querySelector(".bundle-products__error-message");
         this.variantSelects = this.querySelectorAll(".bundle-products__variant-select");
         this.variantInputs = this.querySelectorAll(".bundle-products__variant-id");
         this.hotspots = this.querySelectorAll(".bundle-products__hotspot");

         this.init();
      }

      init() {
         // Initialize products data
         this.initializeProducts();

         // Add event listeners for all add to cart buttons
         this.addButtons.forEach((button) => {
            button.addEventListener("click", this.handleAddToCart.bind(this));
         });

         this.variantSelects.forEach((select) => {
            select.addEventListener("change", this.handleVariantChange.bind(this));
         });

         this.hotspots.forEach((hotspot) => {
            hotspot.addEventListener("click", this.handleHotspotClick.bind(this));
         });

         // Add event listeners for quick add buttons
         const quickAddButtons = this.querySelectorAll(".bundle-products__quick-add-button");
         quickAddButtons.forEach((button) => {
            button.addEventListener("click", this.handleQuickAdd.bind(this));
         });

         // Initialize carousel controls if present
         this.carouselTrack = this.querySelector(".bundle-products__carousel-track");
         this.prevButtons = this.querySelectorAll(".shop-the-look--prev");
         this.nextButtons = this.querySelectorAll(".shop-the-look--next");
         if (this.carouselTrack) this.initCarousel();
      }

      initCarousel() {
         // Attach listeners to prev/next buttons and update state
         const track = this.carouselTrack;

         this.prevButtons.forEach((btn) =>
            btn.addEventListener("click", (e) => {
               e.preventDefault();
               this.scrollPrev();
            })
         );
         this.nextButtons.forEach((btn) =>
            btn.addEventListener("click", (e) => {
               e.preventDefault();
               this.scrollNext();
            })
         );

         // Update buttons' disabled state on scroll/resize
         track.addEventListener("scroll", this.updateCarouselButtons.bind(this));
         window.addEventListener("resize", this.updateCarouselButtons.bind(this));

         // Initial state
         this.updateCarouselButtons();
      }

      scrollNext() {
         const track = this.carouselTrack;
         if (!track) return;
         const firstSlide = track.querySelector(".bundle-products__grid");
         const step = firstSlide ? firstSlide.offsetWidth : track.clientWidth;
         track.scrollBy({ left: step, behavior: "smooth" });
      }

      scrollPrev() {
         const track = this.carouselTrack;
         if (!track) return;
         const firstSlide = track.querySelector(".bundle-products__grid");
         const step = firstSlide ? firstSlide.offsetWidth : track.clientWidth;
         track.scrollBy({ left: -step, behavior: "smooth" });
      }

      updateCarouselButtons() {
         const track = this.carouselTrack;
         if (!track) return;
         const maxScrollLeft = track.scrollWidth - track.clientWidth - 1; // small tolerance
         const atStart = track.scrollLeft <= 0;
         const atEnd = track.scrollLeft >= maxScrollLeft;

         this.prevButtons.forEach((btn) => {
            btn.disabled = atStart;
            btn.setAttribute("aria-disabled", atStart ? "true" : "false");
         });
         this.nextButtons.forEach((btn) => {
            btn.disabled = atEnd;
            btn.setAttribute("aria-disabled", atEnd ? "true" : "false");
         });
      }

      initializeProducts() {
         this.variantInputs.forEach((input, index) => {
            const productId = input.closest("[data-product-id]")?.dataset.productId;
            const variantId = input.value;
            const isAvailable = input.dataset.productAvailable === "true";

            if (productId && variantId) {
               this.products.push({
                  index,
                  productId,
                  variantId,
                  isAvailable,
               });

               this.selectedVariants.set(index, variantId);
            }
         });
      }

      handleVariantChange(event) {
         const select = event.target;
         const productIndex = parseInt(select.dataset.productIndex);
         const productItem = select.closest(".bundle-products__item");

         if (!productItem) return;

         // Get all variant selects for this product
         const allSelects = productItem.querySelectorAll(".bundle-products__variant-select");
         const selectedOptions = Array.from(allSelects).map((s) => s.value);

         // Get the variants data for this product
         const variantsDataElement = productItem.querySelector(`.bundle-products__variants-data[data-product-index="${productIndex}"]`);
         if (!variantsDataElement) return;

         let variants;
         try {
            variants = JSON.parse(variantsDataElement.textContent);
         } catch (error) {
            console.error("Error parsing variants data:", error);
            return;
         }

         // Update available options for other selects
         this.updateAvailableOptions(productItem, variants, selectedOptions);

         // Find the matching variant based on selected options
         const matchingVariant = variants.find((variant) => {
            return variant.options.every((option, index) => option === selectedOptions[index]);
         });

         // Update the hidden variant ID input
         const variantInput = productItem.querySelector(".bundle-products__variant-id");
         if (variantInput && matchingVariant) {
            variantInput.value = matchingVariant.id;
            variantInput.dataset.productAvailable = matchingVariant.available ? "true" : "false";

            // Update the stored variant in our products array
            this.selectedVariants.set(productIndex, matchingVariant.id);

            // Update the price display if available
            const priceElement = productItem.querySelector(".price__regular .price-item--regular");
            if (priceElement && matchingVariant.price) {
               // Format the price
               if (window.Shopify && window.Shopify.formatMoney) {
                  priceElement.textContent = window.Shopify.formatMoney(matchingVariant.price);
               }
            }

            // Update quick add button state and text
            const quickAddButton = productItem.querySelector(".bundle-products__quick-add-button");
            const quickAddText = productItem.querySelector(".bundle-products__quick-add-text");
            if (quickAddButton && quickAddText) {
               quickAddButton.disabled = !matchingVariant.available;
               quickAddText.textContent = matchingVariant.available ? quickAddText.dataset.addText || "Quick add" : quickAddText.dataset.soldOutText || "Sold out";
            }
         }
      }

      updateAvailableOptions(productItem, variants, selectedOptions) {
         const allSelects = productItem.querySelectorAll(".bundle-products__variant-select");

         allSelects.forEach((select, selectIndex) => {
            const options = select.querySelectorAll("option");

            options.forEach((option) => {
               const optionValue = option.value;

               // Check if this option value is available given the current selections
               const isAvailable = variants.some((variant) => {
                  // Check if variant matches current selections for other options
                  const matchesOtherSelections = selectedOptions.every((selectedValue, index) => {
                     if (index === selectIndex) {
                        // For the current select, check if variant has this option value
                        return variant.options[index] === optionValue;
                     } else {
                        // For other selects, check if variant matches selected value
                        return variant.options[index] === selectedValue;
                     }
                  });

                  return matchesOtherSelections && variant.available;
               });

               // Update disabled state
               option.disabled = !isAvailable;

               // Update option text to show sold out status
               if (!isAvailable && !option.textContent.includes("Sold out")) {
                  const baseText = optionValue;
                  option.textContent = `${baseText} - Sold out`;
               } else if (isAvailable && option.textContent.includes(" - Sold out")) {
                  option.textContent = optionValue;
               }
            });
         });
      }

      handleHotspotClick(event) {
         const hotspot = event.currentTarget;
         const productIndex = hotspot.dataset.productIndex;

         // Scroll to the corresponding product
         const productItem = this.querySelector(`[data-product-index="${productIndex}"]`);
         if (productItem) {
            productItem.scrollIntoView({ behavior: "smooth", block: "center" });

            // Add highlight effect
            productItem.style.transition = "box-shadow 0.3s ease";
            productItem.style.boxShadow = "0 0 0 2px rgba(var(--color-foreground), 0.3)";

            setTimeout(() => {
               productItem.style.boxShadow = "";
            }, 1500);
         }
      }

      async handleQuickAdd(event) {
         const button = event.currentTarget;
         const productIndex = parseInt(button.dataset.productIndex);
         const productItem = button.closest(".bundle-products__item");

         if (!productItem || button.disabled) return;

         // Get the variant ID
         const variantInput = productItem.querySelector(".bundle-products__variant-id");
         if (!variantInput || !variantInput.value) return;

         const variantId = variantInput.value;

         // Show loading state
         const buttonText = button.querySelector(".bundle-products__quick-add-text");
         const buttonSpinner = button.querySelector(".bundle-products__quick-add-spinner");

         button.disabled = true;
         buttonText?.classList.add("hidden");
         buttonSpinner?.classList.remove("hidden");

         try {
            // Add to cart via Shopify AJAX API
            const items = [
               {
                  id: variantId,
                  quantity: 1,
               },
            ];

            // guard duplicate adds for same items
            const _quickAddKey = JSON.stringify(items);
            if (!window.cartAddGuard.start(_quickAddKey)) {
               // already in-flight
               button.disabled = false;
               buttonText?.classList.remove("hidden");
               buttonSpinner?.classList.add("hidden");
               return;
            }

            const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
               method: "POST",
               headers: {
                  "Content-Type": "application/json",
               },
               body: JSON.stringify({ items }),
            });

            if (!response.ok) {
               const error = await response.json();
               throw new Error(error.description || "Failed to add to cart");
            }

            // Update cart drawer contents (same approach as other add-to-cart handlers)
            const cartDrawer = document.querySelector("cart-drawer");
            if (cartDrawer) {
               const sectionsToRender = cartDrawer.getSectionsToRender ? cartDrawer.getSectionsToRender() : [];
               const sectionsParam = sectionsToRender.map((section) => section.section).join(",");

               try {
                  const cartResponse = await fetch(`${window.location.pathname}?sections=${sectionsParam}`);
                  const cartSections = await cartResponse.json();

                  cartDrawer.renderContents({ sections: cartSections }, true);
               } catch (error) {
                  // If fetching sections fails, fallback to simply opening the drawer
                  if (typeof cartDrawer.open === "function") cartDrawer.open();
               }
            } else {
               // Fallback: dispatch the legacy event so other listeners (if any) can react
               document.documentElement.dispatchEvent(
                  new CustomEvent("cart:refresh", {
                     bubbles: true,
                  })
               );
            }
         } catch (error) {
            console.error("Error adding product to cart:", error);
            alert(error.message || "Failed to add product to cart");
         } finally {
            // release guard
            try { window.cartAddGuard.end(typeof _quickAddKey !== 'undefined' ? _quickAddKey : null); } catch (e) {}
            // Reset button state
            button.disabled = false;
            buttonText?.classList.remove("hidden");
            buttonSpinner?.classList.add("hidden");
         }
      }

      async handleAddToCart(event) {
         const button = event.currentTarget;
         const blockId = button.dataset.blockId;

         console.log("handleAddToCart called for block:", blockId);

         if (!button || button.disabled) return;

         // Get the specific block container
         const blockContainer = this.querySelector(`[data-block-id="${blockId}"].bundle-products__grid`);
         if (!blockContainer) {
            console.error("Block container not found for block:", blockId);
            return;
         }

         // Get elements specific to this button
         const addButtonText = button.querySelector(".bundle-products__add-button-text");
         const addButtonSpinner = button.querySelector(".bundle-products__add-button-spinner");

         // Hide error
         this.hideError();

         // Show loading state
         button.disabled = true;
         addButtonText?.classList.add("hidden");
         addButtonSpinner?.classList.remove("hidden");

         try {
            // Collect all variant IDs from THIS block only
            const items = [];
            const variantInputs = blockContainer.querySelectorAll(".bundle-products__variant-id");

            console.log("Found variant inputs:", variantInputs.length);

            variantInputs.forEach((input) => {
               const variantId = input.value;
               console.log("Variant ID:", variantId);
               if (variantId) {
                  items.push({
                     id: variantId,
                     quantity: 1,
                  });
               }
            });

            console.log("Items to add:", items);

            if (items.length === 0) {
               throw new Error("No products selected");
            }

            // Add to cart via Shopify AJAX API
            const _addKey = JSON.stringify(items);
            if (!window.cartAddGuard.start(_addKey)) {
               // duplicate in-flight
               button.disabled = false;
               addButtonText?.classList.remove("hidden");
               addButtonSpinner?.classList.add("hidden");
               return;
            }

            const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
               method: "POST",
               headers: {
                  "Content-Type": "application/json",
               },
               body: JSON.stringify({ items }),
            });

            if (!response.ok) {
               const error = await response.json();
               throw new Error(error.description || "Failed to add to cart");
            }

            const data = await response.json();

            // Update cart drawer contents (same approach as other add-to-cart handlers)
            const cartDrawer = document.querySelector("cart-drawer");
            if (cartDrawer) {
               const sectionsToRender = cartDrawer.getSectionsToRender ? cartDrawer.getSectionsToRender() : [];
               const sectionsParam = sectionsToRender.map((section) => section.section).join(",");

               try {
                  const cartResponse = await fetch(`${window.location.pathname}?sections=${sectionsParam}`);
                  const cartSections = await cartResponse.json();

                  cartDrawer.renderContents({ sections: cartSections }, true);
               } catch (error) {
                  // If fetching sections fails, fallback to simply opening the drawer
                  if (typeof cartDrawer.open === "function") cartDrawer.open();
               }
            } else {
               // Fallback: dispatch the legacy event so other listeners (if any) can react
               document.documentElement.dispatchEvent(
                  new CustomEvent("cart:refresh", {
                     bubbles: true,
                  })
               );
            }

            // Reset loading state
            try { window.cartAddGuard.end(typeof _addKey !== 'undefined' ? _addKey : null); } catch (e) {}
            button.disabled = false;
            addButtonText?.classList.remove("hidden");
            addButtonSpinner?.classList.add("hidden");
         } catch (error) {
            console.error("Error adding bundle to cart:", error);
            this.showError(error.message || "Failed to add bundle to cart");

            // Reset loading state
            button.disabled = false;
            addButtonText?.classList.remove("hidden");
            addButtonSpinner?.classList.add("hidden");
         }
      }

      showError(message) {
         if (this.errorElement && this.errorMessageElement) {
            this.errorMessageElement.textContent = message;
            this.errorElement.hidden = false;
         }
      }

      hideError() {
         if (this.errorElement) {
            this.errorElement.hidden = true;
         }
      }
   }

   customElements.define("bundle-products", BundleProducts);
}
